import type { Factor, Level } from '@shared/factors';

export interface FactorScoreInput {
  factor_id: number;
  level: Level | null;
}

export type Verdict = 'A' | 'B' | 'C' | 'D';

export interface ScoreSummary {
  total: number | null;
  verdict: Verdict | null;
  byLayer: Record<string, number | null>;
  byCategory: Record<string, { name: string; score: number | null; count: number }>;
  distribution: { lv1: number; lv2: number; lv3: number; lv4_5: number; unanswered: number };
  filledCount: number;
  totalCount: number;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

export function average(levels: (Level | null | undefined)[]): number | null {
  const nums = levels.filter((l): l is Level => typeof l === 'number');
  if (nums.length === 0) return null;
  return round1(nums.reduce((a, b) => a + b, 0) / nums.length);
}

export function verdictOf(total: number | null): Verdict | null {
  if (total === null) return null;
  if (total >= 4.0) return 'A';
  if (total >= 3.0) return 'B';
  if (total >= 2.0) return 'C';
  return 'D';
}

export function summarize(factors: Factor[], scores: FactorScoreInput[]): ScoreSummary {
  const scoreMap = new Map(scores.map((s) => [s.factor_id, s.level]));

  const allLevels = factors.map((f) => scoreMap.get(f.id) ?? null);
  const total = average(allLevels);

  const byLayer: Record<string, number | null> = {};
  for (const layer of ['I', 'II', 'III', 'IV']) {
    const layerLevels = factors
      .filter((f) => f.layer === layer)
      .map((f) => scoreMap.get(f.id) ?? null);
    byLayer[layer] = average(layerLevels);
  }

  const byCategory: Record<string, { name: string; score: number | null; count: number }> = {};
  for (const f of factors) {
    if (!byCategory[f.category]) {
      byCategory[f.category] = { name: f.category_name, score: null, count: 0 };
    }
  }
  for (const key of Object.keys(byCategory)) {
    const catFactors = factors.filter((f) => f.category === key);
    byCategory[key].count = catFactors.length;
    byCategory[key].score = average(catFactors.map((f) => scoreMap.get(f.id) ?? null));
  }

  const distribution = { lv1: 0, lv2: 0, lv3: 0, lv4_5: 0, unanswered: 0 };
  for (const l of allLevels) {
    if (l === null) distribution.unanswered++;
    else if (l === 1) distribution.lv1++;
    else if (l === 2) distribution.lv2++;
    else if (l === 3) distribution.lv3++;
    else if (l === 4 || l === 5) distribution.lv4_5++;
  }

  const filledCount = allLevels.filter((l) => l !== null).length;

  return {
    total,
    verdict: verdictOf(total),
    byLayer,
    byCategory,
    distribution,
    filledCount,
    totalCount: factors.length,
  };
}
